import React from "react";
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import StudyLevelMetricCoverageCard from "./StudyLevelMetricCoverageCard";
import StudyLevelMetricDetailCard from "./StudyLevelMetricDetailCard";
import StudyLevelMetricCoverageHeatmapCard from "./StudyLevelMetricCoverageHeatmapCard";
import ExportReportModal, { type ExportModule } from "./ExportReportModal";
import { apiService } from "../../../config/api";
import StudyService from "../../../services/studyService";
import type { Participant } from "../../../types";
import type {
  StudyMetricCoverageResponse,
  StudyMetricDetailResponse,
} from "../../../services/dataCoverageService";
import { dataCoverageReportExportService } from "../../../services/dataCoverageReportExportService";
import { htmlReportGenerator } from "../../../utils/dataCoverageHtmlReportGenerator";
import {
  createAndDownloadZip,
  dataUrlToBlob,
} from "../../../utils/dataCoverageZipExport";
import {
  DEFAULT_STUDY_DEVICE_AVAILABILITY,
  deriveStudyDeviceAvailability,
} from "./studyDeviceAvailability";

interface StudyLevelContentProps {
  studyId: string;
}

interface ModuleData {
  coverageData: StudyMetricCoverageResponse | null;
  detailData: StudyMetricDetailResponse | null;
  studyName: string;
}

function captureHeatmapPng(chartContainerId: string): string | null {
  const container = document.getElementById(chartContainerId);
  const canvas = container?.querySelector("canvas") as HTMLCanvasElement | null;
  if (!canvas) return null;

  try {
    return canvas.toDataURL("image/png");
  } catch (error) {
    console.error("Failed to capture heatmap PNG:", error);
    return null;
  }
}

const StudyLevelContent: React.FC<StudyLevelContentProps> = ({ studyId }) => {
  const [availabilityLoading, setAvailabilityLoading] = React.useState(true);
  const [availabilityError, setAvailabilityError] = React.useState<
    string | null
  >(null);
  const [deviceAvailability, setDeviceAvailability] = React.useState(
    DEFAULT_STUDY_DEVICE_AVAILABILITY,
  );
  const [moduleData, setModuleData] = React.useState<ModuleData>({
    coverageData: null,
    detailData: null,
    studyName: "",
  });
  const [exportModalOpen, setExportModalOpen] = React.useState(false);
  const [isExporting, setIsExporting] = React.useState(false);

  React.useEffect(() => {
    let isMounted = true;

    const fetchParticipants = async () => {
      if (!studyId) {
        if (isMounted) {
          setAvailabilityLoading(false);
          setAvailabilityError("Missing study identifier.");
          setDeviceAvailability(DEFAULT_STUDY_DEVICE_AVAILABILITY);
        }
        return;
      }

      try {
        if (isMounted) {
          setAvailabilityLoading(true);
          setAvailabilityError(null);
        }

        const token = await apiService.getAuthToken();
        if (!token) {
          if (isMounted) {
            setAvailabilityLoading(false);
            setAvailabilityError("Sign in to load study participants.");
          }
          return;
        }

        const response = await StudyService.getStudyParticipants(
          studyId,
          token,
        );
        if (!isMounted) return;

        if (response.success && Array.isArray((response as any).participants)) {
          const participants: Participant[] = (response as any).participants;
          setDeviceAvailability(deriveStudyDeviceAvailability(participants));
          // Get study name
          const studyResponse = await StudyService.getStudyStats(
            studyId,
            token,
          );
          if (
            studyResponse.success &&
            (studyResponse as any).study?.studyName
          ) {
            setModuleData((prev) => ({
              ...prev,
              studyName: (studyResponse as any).study.studyName,
            }));
          }
        } else {
          setAvailabilityError(
            "Unable to determine connected Fitbit/HealthKit participants.",
          );
          setDeviceAvailability(DEFAULT_STUDY_DEVICE_AVAILABILITY);
        }
      } catch (_err) {
        if (!isMounted) return;
        setAvailabilityError(
          "Failed to load participant device availability. Device options may be unavailable.",
        );
        setDeviceAvailability(DEFAULT_STUDY_DEVICE_AVAILABILITY);
      } finally {
        if (isMounted) {
          setAvailabilityLoading(false);
        }
      }
    };

    fetchParticipants();

    return () => {
      isMounted = false;
    };
  }, [studyId]);

  const handleCoverageDataChange = (data: unknown) => {
    setModuleData((prev) => ({
      ...prev,
      coverageData: (data as StudyMetricCoverageResponse | null) ?? null,
    }));
  };

  const handleDetailDataChange = (data: unknown) => {
    setModuleData((prev) => ({
      ...prev,
      detailData: (data as StudyMetricDetailResponse | null) ?? null,
    }));
  };

  const handleExport = async (selectedModuleIds: string[]) => {
    try {
      setIsExporting(true);
      const files: Array<{ name: string; content: string | Blob }> = [];
      const timestamp = new Date().toISOString().slice(0, 10);

      // Export selected modules
      if (selectedModuleIds.includes("coverage") && moduleData.coverageData) {
        const summaryJson =
          dataCoverageReportExportService.generateStudySummaryJSON(
            moduleData.coverageData,
            moduleData.studyName,
          );
        files.push({
          name: `study_summary_${timestamp}.json`,
          content: JSON.stringify(summaryJson, null, 2),
        });
      }

      if (selectedModuleIds.includes("detail") && moduleData.detailData) {
        const detailJson =
          dataCoverageReportExportService.generateStudyMetricDetailJSON(
            moduleData.detailData,
            moduleData.studyName,
          );
        files.push({
          name: `study_metrics_detail_${timestamp}.json`,
          content: JSON.stringify(detailJson, null, 2),
        });
      }

      // Generate HTML report
      const heatmapImageDataUrl = captureHeatmapPng(
        "study-coverage-heatmap-chart",
      );

      if (heatmapImageDataUrl) {
        const pngBlob = dataUrlToBlob(heatmapImageDataUrl);
        if (pngBlob) {
          files.push({
            name: `study_coverage_heatmap_${timestamp}.png`,
            content: pngBlob,
          });
        }
      }

      const htmlContent = htmlReportGenerator.generateStudyReport(
        moduleData.coverageData
          ? dataCoverageReportExportService.generateStudySummaryJSON(
              moduleData.coverageData,
              moduleData.studyName,
            )
          : {
              metadata: {
                exportedAt: new Date().toISOString(),
                studyId: studyId,
                studyName: moduleData.studyName,
                jsonVersion: "1.0",
              },
              summaryStats: {
                dateRange: {
                  fromDate: "",
                  endDate: "",
                  totalDays: 0,
                },
                participantsAnalyzed: 0,
                avgCoveragePct: 0,
                logs: [],
              },
              metricsOverview: [],
            },
        moduleData.detailData
          ? dataCoverageReportExportService.generateStudyMetricDetailJSON(
              moduleData.detailData,
              moduleData.studyName,
            )
          : undefined,
        heatmapImageDataUrl || undefined,
      );

      files.push({
        name: `study_report_${timestamp}.html`,
        content: htmlContent,
      });

      // Create and download ZIP
      await createAndDownloadZip(
        files,
        `study-report-${studyId}-${timestamp}.zip`,
      );
    } catch (error) {
      console.error("Export error:", error);
      throw error;
    } finally {
      setIsExporting(false);
    }
  };

  const modules: ExportModule[] = [
    {
      id: "coverage",
      label: "Study Metric Coverage",
      hasData: !!moduleData.coverageData,
      description: "Summary metrics overview and coverage statistics",
    },
    {
      id: "detail",
      label: "Study Metric Details",
      hasData: !!moduleData.detailData,
      description: "Detailed metrics analysis with participant breakdown",
    },
  ];

  if (availabilityLoading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Loading Study-Level Metrics
          </Typography>
          <LinearProgress variant="indeterminate" />
          <Typography variant="body2" color="textSecondary">
            Fetching participant device availability...
          </Typography>
        </Stack>
      </Paper>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          startIcon={<FileDownloadIcon />}
          onClick={() => setExportModalOpen(true)}
          sx={{
            backgroundColor: "#8C1515",
            "&:hover": { backgroundColor: "#B33A3A" },
            px: 3,
            py: 1,
            fontWeight: 600,
          }}
        >
          Export Report
        </Button>
      </Box>

      {availabilityError && (
        <Alert severity="warning">{availabilityError}</Alert>
      )}

      <StudyLevelMetricCoverageCard
        studyId={studyId}
        deviceAvailability={deviceAvailability}
        onDataChange={handleCoverageDataChange}
      />
      <StudyLevelMetricDetailCard
        studyId={studyId}
        deviceAvailability={deviceAvailability}
        onDataChange={handleDetailDataChange}
      />
      <StudyLevelMetricCoverageHeatmapCard
        studyId={studyId}
        deviceAvailability={deviceAvailability}
      />

      <ExportReportModal
        open={exportModalOpen}
        modules={modules}
        onExport={handleExport}
        onClose={() => setExportModalOpen(false)}
        isExporting={isExporting}
      />
    </Box>
  );
};

export default StudyLevelContent;
